import { useState, useEffect } from 'react';
import { Coordinates, CalculationMethod, PrayerTimes } from 'adhan';
import { Moon, MapPin, Clock, Users, ChevronRight } from 'lucide-react';
import confetti from 'canvas-confetti';

export function RamadanCard() {
  const [ramadanDays, setRamadanDays] = useState<number>(0);
  const [ishaTime, setIshaTime] = useState<Date | null>(null);
  const [tarawehTime, setTarawehTime] = useState<string>('--:--');
  const [loading, setLoading] = useState(true);

  const [eidCountdown, setEidCountdown] = useState<number>(0);

  useEffect(() => {
    // 1. Calculate Ramadan Countdown & Eid Countdown
    const ramadanStart = new Date('2026-02-17T00:00:00');
    const eidAlFitr = new Date('2026-03-20T00:00:00'); // Friday, March 20th 2026
    
    const today = new Date();
    today.setHours(0,0,0,0);
    
    // Days until Ramadan Starts
    const diffRamadan = ramadanStart.getTime() - today.getTime();
    const daysToRamadan = Math.ceil(diffRamadan / (1000 * 60 * 60 * 24));
    setRamadanDays(daysToRamadan);
    
    // Days until Eid (Ramadan Ends)
    const diffEid = eidAlFitr.getTime() - today.getTime();
    const daysToEid = Math.ceil(diffEid / (1000 * 60 * 60 * 24));
    setEidCountdown(daysToEid);

    // 2. Get Isha Time for Taraweh
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const { latitude, longitude } = position.coords;
        const coordinates = new Coordinates(latitude, longitude);
        const params = CalculationMethod.MuslimWorldLeague();
        const pt = new PrayerTimes(coordinates, new Date(), params);
        
        setIshaTime(pt.isha);
        
        // Estimate Taraweh: Isha + 15 mins
        const taraweh = new Date(pt.isha.getTime() + 15 * 60000);
        setTarawehTime(taraweh.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }));
        setLoading(false);
      }, () => setLoading(false));
    } else {
        setLoading(false);
    }
  }, []);

  const handleCardClick = () => {
    // Celebration Confetti!
    const end = Date.now() + 1000;

    const colors = ['#10B981', '#F59E0B', '#ffffff']; // Emerald, Amber, White

    (function frame() {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());
  };

  const handleFindMosque = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    // Open Google Maps searching for nearby mosques
    window.open('https://www.google.com/maps/search/mosque+near+me', '_blank');
  };

  // Logic:
  // - If ramadanDays > 0: Ramadan hasn't started yet. Show "X Days Left" to Ramadan.
  // - If ramadanDays <= 0 AND eidCountdown > 0: Ramadan is ACTIVE. Show "X Days to Eid".
  // - If eidCountdown <= 0: Ramadan/Eid over. Hide card.
  
  // Update logic to hide after Eid today (March 19th 2026, or whatever today is)
  if (ramadanDays > 30 && eidCountdown > 30) return null; // Too far away
  if (eidCountdown < 0) return null; // Hide immediately when Eid is in the past

  const isRamadanActive = ramadanDays <= 0 && eidCountdown > 0;
  const isEid = eidCountdown === 0;

  return (
    <div 
      onClick={handleCardClick}
      className="bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 rounded-3xl p-6 text-white shadow-xl border border-emerald-800/30 relative overflow-hidden group cursor-pointer hover:shadow-emerald-900/20 transition-all active:scale-[0.99]"
    >
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl -mr-10 -mt-10" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl -ml-10 -mb-10" />

      {/* Header */}
      <div className="relative z-10 flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Moon className="w-5 h-5 text-amber-400 fill-amber-400/20" />
            <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
              {isEid ? 'Eid Mubarak!' : (isRamadanActive ? 'Ramadan Kareem' : 'Ramadan 2026')}
            </span>
          </div>
          <h2 className="text-2xl font-bold text-white">
            {isEid 
                ? 'May Allah Accept It' 
                : (isRamadanActive ? (eidCountdown === 1 ? 'Last Day of Ramadan' : `${eidCountdown} Days to Eid`) : `${ramadanDays} Days Left`)}
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            {isRamadanActive ? (eidCountdown === 1 ? 'Prepare for Eid' : 'Make every night count') : 'Prepare for the blessed month'}
          </p>
        </div>
        
        {/* Taraweh Badge - Hide if Eid or night before Eid */}
        {eidCountdown > 1 && (
          <div className="bg-emerald-900/40 backdrop-blur-md border border-emerald-500/20 rounded-xl p-3 text-center min-w-[90px]">
            <span className="text-[10px] text-emerald-200/80 uppercase block mb-0.5 tracking-wider">Taraweh</span>
            <span className="text-xl font-bold text-emerald-300 font-mono">
              {loading ? '--:--' : tarawehTime}
            </span>
          </div>
        )}
      </div>

      {/* Tonight's Schedule - Hide if Eid or night before Eid */}
      {eidCountdown > 1 && (
        <div className="relative z-10 space-y-4 mb-6">
          <h3 className="text-sm font-semibold text-emerald-100/90 uppercase tracking-wide flex items-center gap-2">
              <Clock className="w-4 h-4 text-emerald-400" />
              Tonight's Schedule
          </h3>
          
          <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 border border-white/5 rounded-xl p-3">
                  <span className="text-xs text-slate-400 block mb-1">Isha Prayer</span>
                  <span className="text-lg font-bold text-white block">
                      {ishaTime ? ishaTime.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'}) : '--:--'}
                  </span>
                  <span className="text-[10px] text-emerald-400/80 block mt-1">Arrive 10m early</span>
              </div>
              
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 relative overflow-hidden">
                   <div className="absolute top-0 right-0 w-8 h-8 bg-emerald-400/10 rounded-bl-xl" />
                  <span className="text-xs text-emerald-200 block mb-1">Taraweh Begins</span>
                  <span className="text-lg font-bold text-emerald-300 block">
                      {loading ? '--:--' : tarawehTime}
                  </span>
                  <span className="text-[10px] text-emerald-400/80 block mt-1">Promptly after Isha</span>
              </div>
          </div>
        </div>
      )}

      {/* Eid Schedule - Show on Eid or day before */}
      {eidCountdown <= 1 && (
        <div className="relative z-10 space-y-4 mb-6">
          <h3 className="text-sm font-semibold text-emerald-100/90 uppercase tracking-wide flex items-center gap-2">
              <Clock className="w-4 h-4 text-emerald-400" />
              Eid Schedule
          </h3>
          
          <div className="grid grid-cols-1 gap-3">
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 relative overflow-hidden">
                   <div className="absolute top-0 right-0 w-12 h-12 bg-emerald-400/10 rounded-bl-full" />
                  <span className="text-xs text-emerald-200 block mb-1">Eid Prayer</span>
                  <span className="text-xl font-bold text-emerald-300 block">
                      7:30 AM & 9:00 AM
                  </span>
                  <span className="text-xs text-emerald-400/80 block mt-1">Takbeerat begins 30 mins prior</span>
              </div>
          </div>
        </div>
      )}

      {/* Community Info (Inspired by NWLM) */}
      <div className="relative z-10 space-y-3 bg-slate-950/30 rounded-xl p-4 border border-white/5">
        <div className="flex items-start gap-3">
            <div className="mt-0.5">
                <Users className="w-4 h-4 text-slate-400" />
            </div>
            <div>
                <span className="text-sm font-medium text-slate-200 block">Brothers & Sisters Welcome</span>
                <span className="text-xs text-slate-400 block mt-0.5">Parents must accompany children at all times</span>
            </div>
        </div>
        
        <div className="h-px bg-white/5 w-full" />
        
        <div className="flex items-start gap-3">
            <div className="mt-0.5">
                <MapPin className="w-4 h-4 text-slate-400" />
            </div>
            <div>
                <span className="text-sm font-medium text-slate-200 block">Parking Available</span>
                <span className="text-xs text-slate-400 block mt-0.5">School & Street parking nearby</span>
            </div>
        </div>
      </div>

      {/* Action Button */}
      <div 
        onClick={handleFindMosque}
        className="relative z-10 mt-4 flex items-center justify-center gap-2 text-sm font-medium text-emerald-300 hover:text-emerald-200 cursor-pointer transition-colors py-2 group/btn"
      >
        <span>Find Local Mosque</span>
        <ChevronRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
      </div>
    </div>
  );
}
